import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import AchievementShowcase from '../user-profile/components/AchievementShowcase';
import Button from '../../components/ui/Button';

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // If viewing own profile, redirect to /user-profile
  useEffect(() => {
    if (userId === userProfile?.id) {
      navigate('/user-profile');
    }
  }, [userId, userProfile?.id, navigate]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, avatar_url, bio, location, experience_level, created_at')
          ?.eq('id', userId)
          ?.single();

        if (fetchError) {
          console.error('Error fetching user profile:', fetchError);
          setError('Kullanıcı profili yüklenemedi.');
          return;
        }

        if (!data) {
          setError('Kullanıcı bulunamadı.');
          return;
        }

        // Convert snake_case to camelCase
        const formattedData = {
          id: data?.id,
          fullName: data?.full_name,
          avatarUrl: data?.avatar_url,
          bio: data?.bio,
          location: data?.location,
          experienceLevel: data?.experience_level,
          memberSince: data?.created_at ? new Date(data?.created_at)?.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' }) : 'Bilinmiyor',
          achievements: [] // Add empty achievements array as placeholder
        };

        setProfileData(formattedData);
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('Bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <Icon name="UserX" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {error || 'Kullanıcı Bulunamadı'}
            </h2>
            <p className="text-muted-foreground mb-6">
              Aradığınız profil mevcut değil veya erişim izniniz yok.
            </p>
            <Button onClick={() => navigate('/event-discovery')} variant="primary">
              Ana Sayfaya Dön
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-5xl mx-auto">
          {/* Profile Header */}
          <div className="bg-card rounded-xl shadow-organic p-4 md:p-6 lg:p-8 mb-6">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center">
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-primary/20">
                  {profileData?.avatarUrl ? (
                    <Image
                      src={profileData?.avatarUrl}
                      alt={`${profileData?.fullName} profil fotoğrafı`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10">
                      <Icon name="User" size={48} className="text-primary" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 w-full min-w-0">
                <div className="mb-3">
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-1">
                    {profileData?.fullName || 'İsimsiz Kullanıcı'}
                  </h1>
                </div>

                {profileData?.bio && (
                  <p className="text-sm md:text-base text-foreground/80 mb-4 line-clamp-3">
                    {profileData?.bio}
                  </p>
                )}

                <div className="flex flex-wrap gap-3 md:gap-4">
                  {profileData?.location && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="MapPin" className="w-4 h-4" />
                      <span className="text-sm">{profileData?.location}</span>
                    </div>
                  )}
                  {profileData?.memberSince && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Calendar" className="w-4 h-4" />
                      <span className="text-sm">Üyelik: {profileData?.memberSince}</span>
                    </div>
                  )}
                  {profileData?.experienceLevel && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Award" className="w-4 h-4" />
                      <span className="text-sm">{profileData?.experienceLevel}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Achievement Showcase */}
          <div className="bg-card rounded-xl shadow-organic p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Award" size={24} className="text-primary" />
              Rozetler ve Başarılar
            </h2>
            <AchievementShowcase userId={userId} achievements={profileData?.achievements || []} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;